import { useEffect, useMemo, useState } from 'react';
import { getEffectiveNow, getEffectiveToday, getSystemTimeMode } from '../../utils/systemTime';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(response, label) {
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`${label} failed (${response.status}): ${text || response.statusText}`);
  }
  return text ? JSON.parse(text) : null;
}

async function fetchActiveEnrolments(studentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${studentId}&active=eq.true&select=id,course_id,courses(id,name,class_day,class_time,is_live,course_titles(name),semesters(name))`,
    { headers: apiHeaders() }
  );
  const rows = await readJson(response, 'Loading courses');
  return Array.isArray(rows) ? rows : [];
}

async function fetchWeeksForDate(courseIds, date) {
  if (!courseIds.length) return [];
  const ids = courseIds.join(',');
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=in.(${ids})&class_date=eq.${date}&select=id,course_id,week_number,class_date,attendance_enabled&order=week_number.asc`,
    { headers: apiHeaders() }
  );
  const rows = await readJson(response, 'Loading attendance schedule');
  return Array.isArray(rows) ? rows : [];
}

async function submitMasterTestCheckIn(studentId, courseId, effectiveAt) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/student_attendance_check_in`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        p_student_id: studentId,
        p_course_id: courseId,
        p_test_mode: true,
        p_effective_at: effectiveAt
      })
    }
  );

  return readJson(response, 'Attendance check-in');
}

function courseName(enrolment) {
  return enrolment?.courses?.course_titles?.name ||
    enrolment?.courses?.name ||
    'Untitled Course';
}

function formatEffectiveTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatClock(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

function resultHeading(status) {
  if (status === 'checked_in') return '✓ Checked In';
  if (status === 'already_checked_in') return '✓ Already Checked In';
  if (status === 'not_open') return 'Attendance Check-in Has Not Opened Yet';
  if (status === 'closed') return 'Attendance Check-in Has Closed';
  if (status === 'attendance_disabled') return 'Attendance Check-in Is Not Available';
  if (status === 'no_class') return 'No Class Scheduled';
  if (status === 'not_enrolled') return 'Not Enrolled';
  if (status === 'class_time_missing') return 'Class Time Not Configured';
  return 'Check-in Failed';
}

export default function StudentCheckIn({
  session,
  onBack,
  initialCourseId = '',
  embedded = false
}) {
  const [enrolments, setEnrolments] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(initialCourseId || '');
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const isMaster = !!session?.isMaster;
  const effectiveToday = getEffectiveToday(session);
  const effectiveNow = getEffectiveNow(session);
  const timeMode = getSystemTimeMode(session);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!isMaster) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const rows = await fetchActiveEnrolments(session.studentId);
        if (cancelled) return;

        setEnrolments(rows);
        const courseIds = rows.map(row => row.course_id).filter(Boolean);
        const weekRows = await fetchWeeksForDate(
  courseIds,
  effectiveToday
);
        if (cancelled) return;

        setWeeks(weekRows);

        const preferred = initialCourseId
          ? rows.find(row => row.course_id === initialCourseId)
          : null;
        const scheduled = rows.find(row =>
          weekRows.some(week => week.course_id === row.course_id)
        );
        const initial = preferred || scheduled || rows[0];
        setSelectedCourseId(initial?.course_id || '');
      } catch (err) {
        console.error('Failed to load attendance test data:', err);
        if (!cancelled) setError(err?.message || 'Failed to load attendance information.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [
  isMaster,
  session.studentId,
  initialCourseId,
  effectiveToday
]);

  const selectedEnrolment = useMemo(
    () => enrolments.find(row => row.course_id === selectedCourseId) || null,
    [enrolments, selectedCourseId]
  );

  const selectedWeek = useMemo(
    () => weeks.find(week => week.course_id === selectedCourseId) || null,
    [weeks, selectedCourseId]
  );

  async function handleTestSignal() {
    if (!selectedCourseId || checkingIn) return;

    setCheckingIn(true);
    setError('');
    setResult(null);

    try {
      const response = await submitMasterTestCheckIn(
        session.studentId,
        selectedCourseId,
        getEffectiveNow(session).toISOString()
      );
      setResult(response);
    } catch (err) {
      console.error('Attendance test failed:', err);
      setError(err?.message || 'Attendance check-in failed.');
    } finally {
      setCheckingIn(false);
    }
  }

  return (
    <div style={embedded ? styles.embeddedWrap : styles.wrap}>
      {!embedded && (
        <header style={styles.header}>
          <button type="button" style={styles.backBtn} onClick={onBack}>← Main Menu</button>
          <div style={styles.headerTitle}>Check In</div>
          <div />
        </header>
      )}

      <div style={embedded ? styles.embeddedContent : styles.content}>
        {!isMaster ? (
          <div style={styles.box}>
            <h1 style={styles.heading}>Attendance Check-in</h1>
            <div style={styles.notice}>
              Attendance check-in requires the mobile app.
            </div>
            <div style={styles.mutedText}>
              The mobile version will automatically look for the classroom attendance signal when this page opens.
            </div>
          </div>
        ) : (
          <>
            <div style={styles.testBanner}>
              <div style={styles.testTitle}>🧪 Master Attendance Test</div>
              <div style={styles.testText}>
                This simulates a valid classroom Bluetooth signal. All normal attendance timing rules still apply.
              </div>
              <div style={styles.testMeta}>
                Mode: {timeMode === 'simulated' ? 'Simulated Time' : timeMode === 'open' ? 'Open Everything' : 'Real Time'}
                {' · '}
                Effective time: {formatEffectiveTime(effectiveNow)}
              </div>
              {timeMode === 'open' && (
                <div style={styles.openModeNote}>
                  Open Everything does not bypass attendance timing. Use Simulated Time to test attendance boundaries.
                </div>
              )}
            </div>

            {loading ? (
              <div style={styles.mutedText}>Loading attendance information...</div>
            ) : (
              <div style={styles.box}>
                <h1 style={styles.heading}>Check In</h1>
                <div style={styles.subheading}>Effective date: {effectiveToday}</div>

                {enrolments.length === 0 ? (
                  <div style={styles.notice}>
                    The master student is not actively enrolled in any courses. Add the master student to a test course before testing attendance.
                  </div>
                ) : (
                  <>
                    <label style={styles.label} htmlFor="attendance-test-course">Simulated classroom signal</label>
                    <select
                      id="attendance-test-course"
                      style={styles.select}
                      value={selectedCourseId}
                      onChange={event => {
                        setSelectedCourseId(event.target.value);
                        setResult(null);
                        setError('');
                      }}
                    >
                      {enrolments.map(enrolment => {
                        const week = weeks.find(w => w.course_id === enrolment.course_id);
                        const suffix = week
                          ? week.attendance_enabled
                            ? ' — class today'
                            : ' — attendance disabled'
                          : ' — no class today';
                        return (
                          <option key={enrolment.id} value={enrolment.course_id}>
                            {courseName(enrolment)}{suffix}
                          </option>
                        );
                      })}
                    </select>

                    {selectedEnrolment && (
                      <div style={styles.courseInfo}>
                        <div style={styles.courseName}>{courseName(selectedEnrolment)}</div>
                        <div style={styles.courseMeta}>
                          {selectedEnrolment.courses?.class_day || 'Day not set'}
                          {selectedEnrolment.courses?.class_time && ` · ${selectedEnrolment.courses.class_time.slice(0, 5)}`}
                          {selectedWeek?.week_number && ` · Week ${selectedWeek.week_number}`}
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      style={{ ...styles.checkBtn, ...(checkingIn ? styles.disabledBtn : {}) }}
                      onClick={handleTestSignal}
                      disabled={checkingIn || !selectedCourseId}
                    >
                      {checkingIn ? 'Checking...' : 'Simulate Valid Classroom Signal'}
                    </button>
                  </>
                )}
              </div>
            )}

            {error && <div style={styles.error}>{error}</div>}

            {result && (
              <div style={{
                ...styles.resultBox,
                ...(result.status === 'checked_in' || result.status === 'already_checked_in'
                  ? styles.resultSuccess
                  : styles.resultNeutral)
              }}>
                <div style={styles.resultHeading}>{resultHeading(result.status)}</div>
                {result.course_name && <div style={styles.resultCourse}>{result.course_name}</div>}
                {result.class_date && result.class_time && (
                  <div style={styles.resultLine}>Class: {result.class_date} · {result.class_time}</div>
                )}
                {result.effective_at && (
                  <div style={styles.resultLine}>Effective check-in: {formatEffectiveTime(result.effective_at)}</div>
                )}
                {result.window_opens && result.window_closes && (
                  <div style={styles.resultLine}>
                    Check-in window: {formatClock(result.window_opens)}–{formatClock(result.window_closes)}
                  </div>
                )}
                {result.message && !['checked_in', 'already_checked_in', 'not_open', 'closed'].includes(result.status) && (
                  <div style={styles.resultMessage}>{result.message}</div>
                )}
                {result.is_test && <div style={styles.testRecorded}>TEST ATTENDANCE RECORD</div>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: { minHeight: '100vh', background: '#f4f6f8' },
  embeddedWrap: { minHeight: 0, background: 'transparent' },
  embeddedContent: { maxWidth: '620px', margin: '0 auto', padding: 0 },
  header: {
    background: 'white',
    borderBottom: '1px solid #d7dee5',
    padding: '14px 20px',
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 20
  },
  backBtn: {
    justifySelf: 'start',
    border: '1px solid #d7dee5',
    background: 'white',
    borderRadius: '8px',
    padding: '7px 11px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#215b7d',
    cursor: 'pointer'
  },
  headerTitle: { fontSize: '15px', fontWeight: '700', color: '#1f2933' },
  content: { padding: '20px', maxWidth: '620px', margin: '0 auto' },
  box: {
    background: 'white',
    border: '1px solid #d7dee5',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  heading: { margin: 0, fontSize: '23px', color: '#1f2933' },
  subheading: { marginTop: '5px', marginBottom: '18px', fontSize: '13px', color: '#66788a' },
  testBanner: {
    background: '#fff7ed',
    border: '2px solid #f59e0b',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '14px'
  },
  testTitle: { fontWeight: '800', color: '#92400e', fontSize: '15px' },
  testText: { marginTop: '5px', color: '#78350f', fontSize: '13px', lineHeight: '1.45' },
  testMeta: { marginTop: '7px', color: '#92400e', fontSize: '12px', fontWeight: '700' },
  openModeNote: { marginTop: '7px', color: '#9a3412', fontSize: '12px', fontWeight: '700', lineHeight: '1.4' },
  notice: {
    marginTop: '14px',
    background: '#f7f9fb',
    border: '1px solid #d7dee5',
    borderRadius: '9px',
    padding: '12px 14px',
    color: '#425466',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  mutedText: { color: '#66788a', fontSize: '13px', lineHeight: '1.5' },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '5px', color: '#334e68' },
  select: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 11px',
    border: '1px solid #cfd8df',
    borderRadius: '8px',
    background: 'white',
    fontSize: '14px'
  },
  courseInfo: {
    marginTop: '12px',
    border: '1px solid #e1e7ec',
    borderRadius: '9px',
    padding: '11px 12px',
    background: '#fafbfc'
  },
  courseName: { fontWeight: '800', color: '#1f2933', fontSize: '14px' },
  courseMeta: { marginTop: '3px', color: '#66788a', fontSize: '12px' },
  checkBtn: {
    width: '100%',
    marginTop: '14px',
    border: 'none',
    borderRadius: '9px',
    padding: '12px 15px',
    background: '#215b7d',
    color: 'white',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer'
  },
  disabledBtn: { opacity: 0.6, cursor: 'default' },
  error: {
    background: '#fff1f0',
    color: '#a33',
    border: '1px solid #f0c8c5',
    borderRadius: '9px',
    padding: '12px 14px',
    marginBottom: '14px',
    fontSize: '13px'
  },
  resultBox: { borderRadius: '12px', padding: '18px', border: '2px solid' },
  resultSuccess: { background: '#f0fdf4', borderColor: '#86efac' },
  resultNeutral: { background: '#fff', borderColor: '#d7dee5' },
  resultHeading: { fontSize: '20px', fontWeight: '900', color: '#1f2933' },
  resultCourse: { marginTop: '7px', fontSize: '15px', fontWeight: '800', color: '#334e68' },
  resultLine: { marginTop: '5px', color: '#526679', fontSize: '13px' },
  resultMessage: { marginTop: '8px', color: '#526679', fontSize: '13px', lineHeight: '1.45' },
  testRecorded: {
    display: 'inline-block',
    marginTop: '10px',
    borderRadius: '999px',
    padding: '4px 8px',
    background: '#ffedd5',
    color: '#9a3412',
    fontSize: '10px',
    fontWeight: '900',
    letterSpacing: '0.04em'
  }
};